import { Link, Switch, Route, Redirect, useRouteMatch } from 'react-router-dom';
import type { LinkProps } from 'react-router-dom';

import type { OrderTableSettingsData, useOrderList } from '@frontend/api';
import {
  Box,
  Flex,
  Tabs,
  Tab,
  TabList,
  TabPanels,
  TabPanel,
  TabQuantityBadge,
} from '@frontend/legacy-components';

import { DEFAULT_ORDER_LIST_TAB_ID } from 'modules/orders/listPage/const';
import { useActiveTab } from 'modules/orders/listPage/hooks';

import { CustomizeView, OrderListTable } from './components';
import { ORDER_LIST_TABS_STATIC_DATA } from './const';
import { getOrderListTabs } from './utils';

export type OrderListTabsProps = {
  userId: string;
  orderListQuery: ReturnType<typeof useOrderList>;
  orderTableSettings: OrderTableSettingsData;
};

export const OrderListTabs = ({
  userId,
  orderListQuery,
  orderTableSettings,
}: OrderListTabsProps) => {
  const activeTab = useActiveTab();

  const { path } = useRouteMatch();

  const tabs = Object.values(getOrderListTabs({ orderTableSettings }));

  const activeTabIndex = tabs.findIndex((tab) => tab.tabId === activeTab);

  const { data: orderListData } = orderListQuery;

  const tabCounts = orderListData?.statusCounts;

  return (
    <Tabs
      isManual
      index={activeTabIndex}
      orientation="horizontal"
      size="lg"
      variant="enclosed"
      flexGrow={1}
      borderRadius={{ base: '0', tablet: undefined }}
    >
      <Flex
        justify="space-between"
        alignItems="center"
        position="relative"
        _after={{
          content: '""',
          overflow: 'hidden',
          pointerEvents: 'none',
          zIndex: -1,
          display: 'block',
          position: 'absolute',
          bottom: 0,
          left: 0,
          right: 0,
          height: '1px',
          bg: `separator.border`,
        }}
      >
        <TabList alignItems="center" w="100%">
          {tabs.map((tab) => {
            const toProp: LinkProps['to'] = (location) => {
              const urlSearchParams = new URLSearchParams(location.search);
              urlSearchParams.delete('page');
              return {
                ...location,
                pathname: tab.tabId,
                search: urlSearchParams.toString(),
              };
            };

            const count: number = tabCounts?.[tab.tabCountKey] || 0;

            return (
              <Tab as={Link} to={toProp} key={tab.tabId}>
                {tab.title}
                <TabQuantityBadge>{count}</TabQuantityBadge>
              </Tab>
            );
          })}
          <Box
            ml="auto"
            bg="page.surface"
            px={1}
            position="sticky"
            right={0}
            zIndex={1}
          >
            <CustomizeView
              userId={userId}
              orderTableSettings={orderTableSettings}
            />
          </Box>
        </TabList>
      </Flex>
      <TabPanels display="contents">
        <Switch>
          {tabs.map((tab) => {
            const { tabId, path: tabPath, columns } = tab;

            return (
              <TabPanel key={tabId} as={Route} path={`${path}${tabPath}`}>
                <OrderListTable
                  columns={columns}
                  orderListQuery={orderListQuery}
                  tabId={tabId}
                />
              </TabPanel>
            );
          })}
          <Route
            render={() => {
              const defaultTabPath =
                ORDER_LIST_TABS_STATIC_DATA[DEFAULT_ORDER_LIST_TAB_ID].path;

              return <Redirect to={`${path}${defaultTabPath}`} />;
            }}
          />
        </Switch>
      </TabPanels>
    </Tabs>
  );
};
